import React, { useState } from 'react';
import { CheckCircle2, Flag, Reply as ReplyIcon, FileText, Pencil, Trash2 } from 'lucide-react';
import ReactionBar from './ReactionBar';

interface Reply {
  _id: string;
  content: string;
  author: {
    _id: string;
    businessName: string;
    foto?: string;
    communityProfile?: {
      avatarColor?: string;
      level?: string;
    };
  } | null;
  attachments?: Array<{
    filename?: string;
    originalName?: string;
    url: string;
    mimetype?: string;
    mimeType?: string;
    type?: string;
  }>;
  reactions: Array<{ type: string; user: string }>;
  isAccepted: boolean;
  isAdminReply?: boolean;
  createdAt: string;
  editedAt?: string;
  parentReply?: {
    _id: string;
    author: { businessName: string } | null;
  } | null;
  replyingTo?: {
    _id: string;
    author: { businessName: string } | null;
  } | null;
}

interface ReplyCardProps {
  reply: Reply;
  currentUserId: string;
  isDiscussionAuthor: boolean;
  onReact: (replyId: string, type: string) => void;
  onAccept: (replyId: string) => void;
  onReport?: (replyId: string) => void;
  onReplyToReply?: (replyId: string, authorName: string) => void;
  onPreview?: (preview: { url: string; name: string; type: 'image' | 'pdf' }) => void;
  onEdit?: (replyId: string, content: string) => Promise<void>;
  onDelete?: (replyId: string) => Promise<void>;
  extendedReactions?: boolean;
  /** Whether this is a nested/child reply (shows connecting line to avatar) */
  isChild?: boolean;
}

const timeAgo = (date: string) => {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'adesso';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}g`;
  return new Date(date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
};

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const isImageMime = (mimetype?: string) => (mimetype || '').startsWith('image/');

const highlightMentions = (html: string) =>
  html.replace(
    /@([A-Za-zÀ-ÿ0-9][\w\s.'&-]*?)(?=\s|$|<|,|\.|\))/g,
    '<span class="mention-highlight bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 px-0.5 rounded font-medium">@$1</span>'
  );

const LEVEL_STYLES: Record<string, string> = {
  nuovo: 'text-gray-500 dark:text-gray-400',
  attivo: 'text-blue-600 dark:text-blue-400',
  esperto: 'text-purple-600 dark:text-purple-400',
  top_contributor: 'text-amber-600 dark:text-amber-400',
};
const getLevelStyle = (level?: string) => LEVEL_STYLES[(level || '').toLowerCase()] || LEVEL_STYLES.nuovo;
const getLevelLabel = (level?: string) => {
  const l = (level || '').toLowerCase();
  if (l === 'top_contributor') return 'Top';
  if (l === 'esperto') return 'Esperto';
  if (l === 'attivo') return 'Attivo';
  return '';
};

const getAttUrl = (url: string) =>
  url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/graphql', '') : 'http://localhost:5000'}${url}`;

const ReplyCard: React.FC<ReplyCardProps> = ({
  reply,
  currentUserId,
  isDiscussionAuthor,
  onReact,
  onAccept,
  onReport,
  onReplyToReply,
  onPreview,
  onEdit,
  onDelete,
  extendedReactions,
  isChild,
}) => {
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(reply.content);
  const [saving, setSaving] = useState(false);
  const isStaff = reply.isAdminReply || !reply.author;
  const avatarColor = isStaff ? '#4f46e5' : (reply.author?.communityProfile?.avatarColor || '#6366f1');
  const initials = reply.author?.businessName ? getInitials(reply.author?.businessName) : 'UN';
  const levelLabel = isStaff ? '' : getLevelLabel(reply.author?.communityProfile?.level);
  const isOwn = reply.author?._id === currentUserId;

  const handleSaveEdit = async () => {
    if (!editContent.trim() || saving) return;
    setSaving(true);
    try {
      await onEdit?.(reply._id, editContent.trim());
      setEditing(false);
    } catch {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!window.confirm('Sei sicuro di voler eliminare questo commento?')) return;
    onDelete?.(reply._id);
  };

  return (
    <div className={`flex gap-2.5 group ${reply.isAccepted ? 'relative' : ''}`}>
      {/* Avatar */}
      <div className="flex-shrink-0 pt-0.5 relative">
        {reply.author?.foto ? (
          <img
            src={getAttUrl(reply.author.foto)}
            alt={reply.author.businessName}
            className="w-8 h-8 rounded-full object-cover relative z-[1]"
          />
        ) : (
          <span
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold relative z-[1]"
            style={{ backgroundColor: avatarColor }}
          >
            {initials}
          </span>
        )}
      </div>

      {/* Bubble + actions */}
      <div className="flex-1 min-w-0">
        {/* "In risposta a" indicator */}
        {(reply.replyingTo?.author || reply.parentReply?.author) && isChild && (
          <div className="flex items-center gap-1 mb-0.5 text-[11px] text-gray-400 dark:text-gray-500">
            <ReplyIcon className="w-3 h-3" />
            <span className="font-medium text-indigo-500 dark:text-indigo-400">@{reply.replyingTo?.author?.businessName || reply.parentReply?.author?.businessName}</span>
          </div>
        )}

        {/* Comment bubble */}
        {editing ? (
          <div className="max-w-full">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full px-3 py-2 text-[13px] border border-indigo-300 dark:border-indigo-600 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 resize-none"
              rows={3}
            />
            <div className="flex items-center gap-2 mt-1">
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editContent.trim()}
                className="px-3 py-1 text-[12px] font-medium bg-indigo-600 text-white rounded-full hover:bg-indigo-700 disabled:opacity-40 transition-colors"
              >
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
              <button
                onClick={() => { setEditing(false); setEditContent(reply.content); }}
                className="px-3 py-1 text-[12px] font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                Annulla
              </button>
            </div>
          </div>
        ) : (
          <div className={`inline-block max-w-full rounded-2xl px-3.5 py-2 ${
            isStaff
              ? 'bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-200 dark:ring-indigo-800/40'
              : reply.isAccepted
                ? 'bg-green-50 dark:bg-green-900/10 ring-1 ring-green-200 dark:ring-green-800/50'
                : 'bg-gray-100 dark:bg-gray-700/60'
          }`}>
            {/* Author name + level */}
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className={`text-[12px] font-bold leading-tight ${isStaff ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-900 dark:text-white'}`}>
                {reply.author?.businessName || 'UNASCA Staff'}
              </span>
              {isStaff && (
                <span className="px-1.5 py-0.5 text-[9px] font-bold bg-indigo-100 dark:bg-indigo-800/40 text-indigo-600 dark:text-indigo-300 rounded-full">STAFF</span>
              )}
              {levelLabel && (
                <span className={`text-[10px] font-medium ${getLevelStyle(reply.author?.communityProfile?.level)}`}>
                  {levelLabel}
                </span>
              )}
            </div>

            {/* Content */}
            <div
              className="text-[13px] text-gray-800 dark:text-gray-200 leading-snug break-words [&_p]:mb-0"
              dangerouslySetInnerHTML={{ __html: highlightMentions(reply.content) }}
            />
          </div>
        )}

        {/* Accepted badge */}
        {reply.isAccepted && (
          <div className="inline-flex items-center gap-1 mt-1 px-2.5 py-1 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-[11px] font-semibold">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Risposta accettata
          </div>
        )}

        {/* Attachments */}
        {reply.attachments && reply.attachments.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {reply.attachments.map((att, i) => {
              const mime = att.mimetype || att.mimeType || '';
              const isImg = att.type === 'image' || isImageMime(mime);
              const isPdf = mime === 'application/pdf' || (att.type === 'document' && (att.originalName || att.filename || '').toLowerCase().endsWith('.pdf'));
              const name = att.originalName || att.filename || 'file';
              const url = getAttUrl(att.url);
              return isImg ? (
                <button
                  key={i}
                  onClick={() => onPreview?.({ url, name, type: 'image' })}
                  className="block max-w-[280px] rounded-xl overflow-hidden hover:opacity-90 transition-opacity cursor-pointer"
                >
                  <img src={url} alt={name} className="w-full max-h-[200px] object-cover rounded-xl" />
                </button>
              ) : isPdf ? (
                <button
                  key={i}
                  onClick={() => onPreview?.({ url, name, type: 'pdf' })}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                >
                  <FileText className="w-3.5 h-3.5 text-red-500" />
                  {name}
                </button>
              ) : (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <FileText className="w-3.5 h-3.5" />
                  {name}
                </a>
              );
            })}
          </div>
        )}

        {/* Facebook-style action row: Mi piace · Rispondi · time */}
        <div className="flex items-center gap-2.5 mt-1 pl-1">
          <span className="text-[11px] text-gray-400 dark:text-gray-500">
            {timeAgo(reply.createdAt)}
            {reply.editedAt && <span className="ml-1 italic">(mod.)</span>}
          </span>

          {/* Facebook-style Mi piace with hover emoji picker */}
          <ReactionBar
            reactions={reply.reactions}
            currentUserId={currentUserId}
            onReact={(type) => onReact(reply._id, type)}
            facebook
            extended={extendedReactions}
          />

          {onReplyToReply && (
            <button
              onClick={() => onReplyToReply(reply._id, reply.author?.businessName || 'UNASCA')}
              className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              Rispondi
            </button>
          )}

          {isDiscussionAuthor && (
            <button
              onClick={() => onAccept(reply._id)}
              className={`text-[11px] font-semibold transition-colors ${
                reply.isAccepted
                  ? 'text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400'
                  : 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300'
              }`}
            >
              {reply.isAccepted ? 'Rimuovi' : 'Accetta'}
            </button>
          )}

          {/* Hover-visible actions */}
          {isOwn && (
            <button
              onClick={() => { setEditContent(reply.content); setEditing(true); }}
              className="text-[11px] text-gray-400 dark:text-gray-500 hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors opacity-0 group-hover:opacity-100"
              title="Modifica"
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
          {isOwn && (
            <button
              onClick={handleDelete}
              className="text-[11px] text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
              title="Elimina"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={() => onReport?.(reply._id)}
            className="text-[11px] text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
            title="Segnala"
          >
            <Flag className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReplyCard;
export type { Reply };
